import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { NgForm } from '@angular/forms';
import { FormalizeCompany, FormalizeRequisition } from 'src/app/core/models/formalizePayload';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { JobService } from 'src/app/core/services/job.service';
import { RequisitionService } from 'src/app/core/services/requisition.service';
import { UibModalService } from 'src/app/core/services/uibModalService';
import { UtilityService } from 'src/app/core/services/utility.service';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';
import { UiError } from 'src/app/public/app/models/uiError';

declare var $: any;

@Component({
    selector: 'app-edit-shared-req-page',
    templateUrl: './edit-shared-req-page.component.html',
    styleUrls: ['./edit-shared-req-page.component.css'],
    preserveWhitespaces: true
})
export class EditSharedReqPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() $transition$: any;
    @ViewChild('step1Form') step1Form: NgForm;

    MESSAGECONSTANTS: any = MESSAGECONSTANTS;

    stateParams: any;
    jobId: any;
    buId: any;
    jobName: string;
    jobType: string;
    requisitionInfo: any;
    companyId: any;

    recruiterList: any[] = [];
    selectedRecruiters: any[] = [];
    assignedRecruiterIds: any[] = [];
    requisitionNote = '';
    companyLicensePreference: any;
    formalizeActivityPayload: any;
    step1DetailsTempObject: any = {};
    currentJobStatus = 'OPEN';

    isTransitionAbortRequired = true;
    isLoading = false;
    loadingFlag = true;
    showWizardSteps = false;
    savingFlag = false;
    loadingRecruitersFlag = false;
    step1RequestWasInProgressFlag = false;
    disablePrevButtonFlag = false;
    disableSaveButtonFlag = true;
    disableNextButtonFlag = true;
    disableCancelButtonFlag = false;
    disableAllButtonsFlag = false;

    step1Error: UiError;
    step2Error: UiError;

    private wizardEl: any;
    private pendingInitialLoads = 2;

    constructor(
        public $state: StateService,
        private requisitionService: RequisitionService,
        private authService: AuthService,
        private jobService: JobService,
        private utilityService: UtilityService,
        private alertsAndNotificationsService: AlertAndNotificationsService,
        private uibModalService: UibModalService,
        private elementRef: ElementRef,
        private ngZone: NgZone
    ) { }

    ngOnInit() {
        this.stateParams = this.$transition$.params();
        this.jobId = this.stateParams.jobId;
        this.buId = this.stateParams.buId;
        this.jobName = this.stateParams.jobName;
        this.jobType = this.stateParams.jobType;
        this.requisitionInfo = this.stateParams.requisitionInfo;
        this.companyId = this.authService.getUserDetails().company.companyId;
        this.step1Error = new UiError();
        this.step2Error = new UiError();
        this.getAssignableRecruitersList(() => {
            this.getAssignedRecruiters(() => {
                this._setAssignedRecruiters();
                this._getSharedRequisitionNote(() => {
                    this._createStepTempObject(2);
                    this._markInitialLoadComplete();
                }, () => this._markInitialLoadComplete());
            }, () => this._markInitialLoadComplete());
        }, () => this._markInitialLoadComplete());
        this.getCorporateCompanyLicensePreference(() => {
            this.disableNextButtonFlag = false;
            this._markInitialLoadComplete();
        }, () => this._markInitialLoadComplete());
        this.setFormalizeActivityPayload();
    }

    ngAfterViewInit() {
        // fuelux takes over the pane markup and the flags it drives are template-bound,
        // so hand off after this change-detection pass rather than during it.
        setTimeout(() => {
            this.wizardEl = $(this.elementRef.nativeElement).find('#uploadReqWizard');
            this.wizardEl.wizard();
            this.showWizardSteps = true;
            this._initStepsWizard();
            this.updateActionButtons();
        });
    }

    ngOnDestroy() {
        if (this.wizardEl) {
            this.wizardEl.off('actionclicked.fu.wizard finished.fu.wizard changed.fu.wizard');
        }
    }

    wizardSaveStepData() {
        const currentStep = this._getCurrentWizardStep();
        if (!this._isStepDataValid(currentStep)) {
            return;
        }
        if (!this._hasStepDataChanged(currentStep)) {
            if (this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage('Nothing to be saved!', 'info');
            }
            return;
        }
        if (this._hasRecruitersBeingUnAssigned()) {
            this._showRecruitersUnAssignedWarning(() => this._saveStep1AndReport(currentStep), () => this._setAssignedRecruiters());
        } else {
            this._saveStep1AndReport(currentStep);
        }
    }

    saveStep1Details(successCallback?: () => void, errorCallback?: (error: any) => void) {
        this.utilityService.showLoadingModal('Saving...');
        if (!this._hasRecruitersChanged()) {
            this._saveRequisitionNote(successCallback, errorCallback);
            return;
        }
        this.savingFlag = true;
        const step1SaveObject = {
            jobId: this.jobId,
            vendorId: this.companyId,
            recruiterIds: this._getSelectedRecruiterIds()
        };
        this.requisitionService.assignRecruiters(step1SaveObject, () => {
            if (this._hasNoteChanged()) {
                this._saveRequisitionNote(successCallback, errorCallback);
                this._createStepTempObject(2);
            } else {
                this.savingFlag = false;
                if (successCallback) {
                    successCallback();
                }
                this.utilityService.hideLoadingModal();
            }
        }, (error) => {
            this.savingFlag = false;
            if (errorCallback) {
                errorCallback(error);
            }
            this.utilityService.hideLoadingModal();
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
        });
    }

    updateActionButtons() {
        if (this._getCurrentWizardStep() === 1) {
            this.disablePrevButtonFlag = true;
        }
        this.disableSaveButtonFlag = false;
    }

    resetStepData(step: number, successCallback?: () => void) {
        setTimeout(() => {
            if (step === 2) {
                this._setAssignedRecruiters();
            }
            if (successCallback) {
                successCallback();
            }
        }, 100);
    }

    selectOrDeselectAllRecruiters(action: string) {
        this.selectedRecruiters = action === 'selectAll' ? [...this.recruiterList] : [];
    }

    receiveFormalizedComment(formalizedText: string) {
        this.requisitionNote = formalizedText;
    }

    onVoiceTranscript(transcript: string) {
        this.requisitionNote = this.requisitionNote ? this.requisitionNote + ' ' + transcript : transcript;
    }

    redirectToUrl(url: string, isReloadRequired: boolean) {
        this.$state.go(url, null, { reload: isReloadRequired });
    }

    wizardCancel() {
        this.$state.go('missioncontrol.requisitions');
    }

    getAssignableRecruitersList(successCallback?: () => void, errorCallback?: () => void) {
        this.loadingRecruitersFlag = true;
        this.requisitionService.getVendorRecruiters(this.companyId, this.jobId, (data) => {
            this.loadingRecruitersFlag = false;
            data.forEach((val) => {
                val.name = val.firstName;
                if (val.lastName !== null) {
                    val.name += ' ' + val.lastName;
                }
            });
            this.recruiterList = data;
            if (successCallback) {
                successCallback();
            }
        }, (error) => {
            this.loadingRecruitersFlag = false;
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            if (errorCallback) {
                errorCallback();
            }
        });
    }

    getAssignedRecruiters(successCallback?: () => void, errorCallback?: () => void) {
        this.requisitionService.getAssignedVendorRecruiters(this.jobId, this.companyId, (data) => {
            this.assignedRecruiterIds = data;
            if (successCallback) {
                successCallback();
            }
        }, (error) => {
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            if (errorCallback) {
                errorCallback();
            }
        });
    }

    getCorporateCompanyLicensePreference(successCallback?: () => void, errorCallback?: () => void) {
        this.jobService.getSharedJobWorkflowDetails(this.jobId, (data) => {
            this.companyLicensePreference = data;
            if (successCallback) {
                successCallback();
            }
        }, (error) => {
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            if (errorCallback) {
                errorCallback();
            }
        });
    }

    setFormalizeActivityPayload() {
        // The legacy page read a jobMatchRequisitionNum off a candidateJobMatch it never
        // assigned; there is no job match in this page's context, so the id stays null.
        this.formalizeActivityPayload = {
            candidate: null,
            requisition: new FormalizeRequisition(this.jobId, null,
                this.requisitionInfo ? this.requisitionInfo.requisitionActualNumber : null),
            jobMatch: null,
            company: new FormalizeCompany(this.companyId, this.authService.getUserDetails().company.companyName, this.buId,
                this.requisitionInfo ? this.requisitionInfo.corporateBU : null)
        };
    }

    _getSharedRequisitionNote(successCallback?: () => void, errorCallback?: () => void) {
        this.jobService.getSharedRequisitionNote(this.jobId, this.companyId, (data) => {
            this.requisitionNote = data.requisitionNote;
            if (successCallback) {
                successCallback();
            }
        }, (error) => {
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            if (errorCallback) {
                errorCallback();
            }
        });
    }

    private _markInitialLoadComplete() {
        this.pendingInitialLoads--;
        if (this.pendingInitialLoads <= 0) {
            this.loadingFlag = false;
        }
    }

    _setAssignedRecruiters() {
        this.selectedRecruiters = [];
        this.assignedRecruiterIds.forEach((assignedId) => {
            const recruiter = this.recruiterList.find((val) => val.userId === assignedId);
            if (recruiter) {
                this.selectedRecruiters.push(recruiter);
            }
        });
    }

    _createStepTempObject(currentStep: number) {
        if (currentStep === 2) {
            this.step1DetailsTempObject = {
                jobId: this.jobId,
                vendorId: this.companyId,
                recruiterIds: this._getSelectedRecruiterIds(),
                requisitionNote: this.requisitionNote
            };
        }
    }

    _hasStepDataChanged(currentStep: number) {
        if (currentStep !== 2) {
            return false;
        }
        const currentStep1DataObject = {
            jobId: this.jobId,
            vendorId: this.companyId,
            recruiterIds: this._getSelectedRecruiterIds(),
            requisitionNote: this.requisitionNote
        };
        return !_.isEqual(this.step1DetailsTempObject, currentStep1DataObject);
    }

    _hasRecruitersChanged() {
        return !_.isEqual(this.step1DetailsTempObject.recruiterIds, this._getSelectedRecruiterIds());
    }

    _hasNoteChanged() {
        return this.step1DetailsTempObject.requisitionNote !== this.requisitionNote;
    }

    _getSelectedRecruiterIds() {
        return this.selectedRecruiters.map((recruiter) => recruiter.userId);
    }

    _initStepsWizard() {
        // fuelux dispatches these synchronously from its own jQuery handlers, so re-enter
        // the zone before touching anything the template binds to.
        this.wizardEl.on('actionclicked.fu.wizard', (evt, data) => {
            this.ngZone.run(() => this._onWizardActionClicked(evt, data));
        });
        this.wizardEl.on('finished.fu.wizard', () => {
            this.ngZone.run(() => this.$state.go('missioncontrol.requisitions'));
        });
        this.wizardEl.on('changed.fu.wizard', () => {
            this.ngZone.run(() => this.updateActionButtons());
        });
    }

    _isStepDataValid(currentStep: number) {
        if (currentStep !== 2) {
            return true;
        }
        this.step1Form.onSubmit(undefined);
        const isStepInvalid = this.step1Form.invalid || this.selectedRecruiters.length === 0;
        this.step1Error.hasErrorFlag = isStepInvalid;
        this.step1Error.errorMessage = isStepInvalid ? 'Please enter all mandatory fields.' : '';
        return !isStepInvalid;
    }

    _enableStepActionsOnWizard(currentStep: number) {
        if (currentStep === 3) {
            this.step1RequestWasInProgressFlag = false;
        }
        this.disableAllButtonsFlag = false;
    }

    _hasRecruitersBeingUnAssigned() {
        return this._getTempRecruiterIds().length > this.selectedRecruiters.length;
    }

    _showRecruitersUnAssignedWarning(saveCallback: () => void, cancelCallback: () => void) {
        const selectedRecruiterIds = this._getSelectedRecruiterIds();
        const removedNames = this._getTempRecruiterIds()
            .filter((recruiterId) => selectedRecruiterIds.indexOf(recruiterId) === -1)
            .map((recruiterId) => this.recruiterList.find((recruiter) => recruiter.userId === recruiterId))
            .filter((recruiter) => !!recruiter)
            .map((recruiter) => recruiter.name);
        this.alertsAndNotificationsService.showConfirm('Warning!', this._getWarningMessage(removedNames), 'warning', true,
            () => this.ngZone.run(saveCallback),
            () => this.ngZone.run(cancelCallback));
    }

    _getWarningMessage(recruiterNames: string[]) {
        const lastIndex = recruiterNames.length - 1;
        let message = '<b>';
        recruiterNames.forEach((name, index) => {
            if (index === lastIndex) {
                message += name;
            } else if (index === lastIndex - 1) {
                message += name + ' & ';
            } else {
                message += name + ', ';
            }
        });
        return message + '</b> shall be removed as assigned recruiter(s) from all the candidates in the requisition.'
            + ' Do you still want to continue?';
    }

    uiCanExit(transitions: any) {
        const toState = transitions.to().name;
        const toParams = transitions.params();
        const currentStep = this._getCurrentWizardStep();
        if (this._hasStepDataChanged(currentStep) && this.isTransitionAbortRequired && toState !== 'login') {
            transitions.abort();
            const message = 'Unsaved data on the page will be lost. Do you still want to continue?';
            this.alertsAndNotificationsService.showConfirm('Warning!', message, 'warning', true, () => {
                this.ngZone.run(() => {
                    this.isTransitionAbortRequired = false;
                    this.resetStepData(currentStep);
                    if (this.authService.isSwitchClicked) {
                        this.authService.setIsSwitchClicked(false);
                        this.uibModalService.showAllCompanies();
                    } else {
                        this.$state.go(toState, toParams, { reload: true });
                    }
                });
            }, () => {
                this.ngZone.run(() => {
                    if (this.authService.isSwitchClicked) {
                        this.authService.setIsSwitchClicked(false);
                    }
                });
            });
        } else if (this.authService.isSwitchClicked && !this._hasStepDataChanged(currentStep)) {
            transitions.abort();
            // the modal has to open after ui-router has finished unwinding the aborted transition
            setTimeout(() => this.uibModalService.showAllCompanies(), 150);
            this.authService.setIsSwitchClicked(false);
        }
    }

    _saveRequisitionNote(successCallback?: () => void, errorCallback?: (error: any) => void) {
        this.savingFlag = true;
        const payload = {
            jobId: this.jobId,
            companyId: this.companyId,
            note: this.requisitionNote
        };
        this.jobService.addSharedRequisitionNote(payload, () => {
            this.savingFlag = false;
            this._createStepTempObject(2);
            if (successCallback) {
                successCallback();
            }
            this.utilityService.hideLoadingModal();
        }, (error) => {
            this.savingFlag = false;
            if (errorCallback) {
                errorCallback(error);
            }
            this.utilityService.hideLoadingModal();
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
        });
    }

    private _onWizardActionClicked(evt: any, data: any) {
        if (data.direction === 'previous') {
            if (data.step === 2) {
                this._createStepTempObject(data.step - 1);
            }
            return;
        }
        if (data.direction !== 'next' || data.step !== 2) {
            return;
        }
        if (this.step1RequestWasInProgressFlag) {
            // the save started by the previous click has finished and re-triggered next; let it through
            this._enableStepActionsOnWizard(data.step);
            return;
        }
        if (!this._isStepDataValid(data.step)) {
            evt.preventDefault();
            return;
        }
        if (!this._hasStepDataChanged(data.step)) {
            this._createStepTempObject(data.step + 1);
            return;
        }
        if (this._hasRecruitersBeingUnAssigned()) {
            // the confirm is async, so the move has to be held here and re-issued after the save
            evt.preventDefault();
            this._showRecruitersUnAssignedWarning(() => {
                this.step1RequestWasInProgressFlag = true;
                this._saveStep1AndAdvance();
            }, () => {
                this._setAssignedRecruiters();
            });
            return;
        }
        this.step1RequestWasInProgressFlag = true;
        this._saveStep1AndAdvance();
        evt.preventDefault();
        this.disableAllButtonsFlag = true;
    }

    private _saveStep1AndAdvance() {
        this.saveStep1Details(() => {
            this.wizardEl.wizard('next');
            this.disableSaveButtonFlag = true;
            this._enableStepActionsOnWizard(3);
        }, (error) => {
            this._enableStepActionsOnWizard(3);
            if (this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    private _saveStep1AndReport(currentStep: number) {
        this.disableAllButtonsFlag = true;
        this.savingFlag = true;
        this.saveStep1Details(() => {
            this._enableStepActionsOnWizard(3);
            this.disableAllButtonsFlag = false;
            this.savingFlag = false;
            if (this.authService.isOnline) {
                const successMessage = this.currentJobStatus === 'OPEN'
                    ? 'The Requisition details have been saved.'
                    : 'The Requisition details have been saved in Draft Status.';
                this.alertsAndNotificationsService.showBannerMessage(successMessage, 'success');
            }
            this._createStepTempObject(currentStep);
        }, (error) => {
            this.disableAllButtonsFlag = false;
            this.savingFlag = false;
            if (this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    private _getCurrentWizardStep(): number {
        // uiCanExit can fire before ngAfterViewInit has handed the panes to fuelux
        return this.wizardEl ? this.wizardEl.wizard('selectedItem').step : 0;
    }

    private _getTempRecruiterIds(): any[] {
        return this.step1DetailsTempObject.recruiterIds || [];
    }
}
